import { type FC, useMemo, useState } from "react";

import { Box, Card, Pagination, Skeleton, Table, Text } from "@mantine/core";

import { useTrendingOnSpotlight } from "../../trending-on-spotlight/hooks";

import { pagination, sectionTitle, tableHeader } from "./HomeTrendingSection.module.css";
import { NoTrendingItems, TableHeaderTrend, TrendingOnSpotlight } from "./messages.generated";

const PAGE_SIZE = 7;
const SKELETON_ROW_COUNT = 5;
const TEXT_LINE_HEIGHT = 14;

const skeletonRows = Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
    <Table.Tr key={index}>
        <Table.Td w={40}>
            <Skeleton height={TEXT_LINE_HEIGHT} width={20} />
        </Table.Td>
        <Table.Td>
            <Skeleton height={TEXT_LINE_HEIGHT} width="80%" />
        </Table.Td>
    </Table.Tr>
));

export const HomeTrendingOnSpotlightTable: FC = () => {
    const { trends, loading } = useTrendingOnSpotlight();
    const [page, setPage] = useState(1);

    const totalPages = Math.ceil(trends.length / PAGE_SIZE);

    const paginatedTrends = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return trends.slice(start, start + PAGE_SIZE);
    }, [trends, page]);

    const rankOffset = (page - 1) * PAGE_SIZE;

    const rows = useMemo(
        () =>
            paginatedTrends.map((trend, index) => (
                <Table.Tr key={trend.name}>
                    <Table.Td className="paragraph-p300" w={40} c="dimmed">
                        #{rankOffset + index + 1}
                    </Table.Td>
                    <Table.Td className="paragraph-p300">{trend.name}</Table.Td>
                </Table.Tr>
            )),
        [paginatedTrends, rankOffset],
    );

    const showPagination = !loading && totalPages > 1;

    return (
        <Box>
            <h3 className={`heading-h400 ${sectionTitle}`}>
                <TrendingOnSpotlight />
            </h3>
            <Card shadow="sm" radius={0} p={5}>
                <Table verticalSpacing="md" withRowBorders>
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th className={tableHeader} w={40} />
                            <Table.Th className={tableHeader}>
                                <TableHeaderTrend />
                            </Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {loading ? skeletonRows : rows}
                        {!loading && trends.length === 0 && (
                            <Table.Tr>
                                <Table.Td colSpan={2}>
                                    <Text c="dimmed" ta="center" py="md">
                                        <NoTrendingItems />
                                    </Text>
                                </Table.Td>
                            </Table.Tr>
                        )}
                    </Table.Tbody>
                </Table>
                {showPagination && (
                    <Pagination
                        total={totalPages}
                        value={page}
                        onChange={setPage}
                        mt="md"
                        size="sm"
                        color="var(--colors-yellow-v200)"
                        className={pagination}
                    />
                )}
            </Card>
        </Box>
    );
};
